import { useEffect } from 'react';
import urlTemplate from 'url-template';
import type { Uuid } from 'src/apis/api-utilities';
import type { CamaTransactionDetails } from 'src/apis/surrender-service/types';
import type { SurrenderRootRequest } from 'src/apis/surrender-service/use-surrender-root';
import { useAxios } from 'src/hooks/use-axios';
import { Forbidden } from 'src/utils/errors';

type HookConfig = {
  surrenderRoot: SurrenderRootRequest;
  transactionId: Uuid | undefined;
};

type ExecuteProps = {
  url: string;
};

export function useCamaTransaction({
  surrenderRoot,
  transactionId,
}: HookConfig) {
  const request = useAxios(
    (axios, baseConfig, { url }: ExecuteProps) =>
      axios.request<CamaTransactionDetails>({
        ...baseConfig,
        url,
      }),
    { neededOnPageLoad: true }
  );

  useEffect(() => {
    if (!surrenderRoot.response || !transactionId) return;
    if (!surrenderRoot.response.data._links.getTransaction)
      throw new Forbidden();

    const templatedUrl = surrenderRoot.response.data._links.getTransaction.href;
    const url = urlTemplate.parse(templatedUrl).expand({ transactionId });

    request.execute({ url });
    // oxlint-disable-next-line exhaustive-deps
  }, [surrenderRoot.response, transactionId]);

  if (!surrenderRoot.response || !transactionId) return;
  if (!surrenderRoot.response.data._links.getTransaction) throw new Forbidden();

  return request;
}

export type TransactionRequest = ReturnType<typeof useCamaTransaction>;
